// Показателите на „Дневни отчети → Персонални".
//
// Числата са за ЦЕЛИЯ период, не за филтрирания списък: филтърът по сервитьор
// вече живее в самата колона и стеснява таблицата и реда „Общо". Така
// кутийките отговарят на „какво е станало в периода", а редът „Общо" — на
// „какво е останало след филтъра"; двете бройки не си противоречат, защото
// казват различни неща.
//
// Кутийката „Друго" се появява САМО когато има платено извън в брой и карта.
// Иначе двата дяла дават 100 % и трета нулева кутийка би шумяла напразно.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { ReportKpiCard } from "../ReportKpiCard";
import { deltaPercent } from "../reportDelta";
import { pluralLabel } from "../reportPlural";
import {
  isZeroAmount,
  shareOf,
  type PersonalReportsSummary,
} from "./personalReportsMath";

type Props = {
  summary: PersonalReportsSummary;
  /** Предходният равен период — `null`, когато сравнението е изключено. */
  previous: PersonalReportsSummary | null;
  isNarrow: boolean;
};

export const PersonalReportsKpis = ({
  summary,
  previous,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  const hasOther = !isZeroAmount(summary.other);

  // Дялът е закръглен до цяло: „68 %" се чете от разстояние, а стотните на
  // процента не решават нищо в дневен отчет.
  const shareLabel = (part: number) => {
    const share = shareOf(part, summary.total);
    return share === null ? undefined : `${share.toFixed(0)} %`;
  };

  return (
    // Класовете са ЦЕЛИ литерали: Tailwind сканира изходния текст и не вижда
    // име, сглобено с template literal.
    <div
      className={`grid gap-2.5 ${
        isNarrow
          ? "grid-cols-2"
          : hasOther
            ? "grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]"
            : "grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]"
      }`}
    >
      <ReportKpiCard
        kicker={t("reportsPage.dailyKpiRevenue")}
        value={fmtEuro(summary.total)}
        sub={pluralLabel(
          t,
          summary.bills,
          "reportsPage.dailyBillsOne",
          "reportsPage.dailyBillsMany",
        )}
        delta={
          previous ? deltaPercent(summary.total, previous.total) : undefined
        }
        hero
      />

      <ReportKpiCard
        kicker={t("reportsPage.cashAmount")}
        value={fmtEuro(summary.cash)}
        sub={shareLabel(summary.cash)}
      />

      <ReportKpiCard
        kicker={t("reportsPage.cardAmount")}
        value={fmtEuro(summary.card)}
        sub={shareLabel(summary.card)}
      />

      {hasOther ? (
        <ReportKpiCard
          kicker={t("reportsPage.dailyOther")}
          value={fmtEuro(summary.other)}
          sub={shareLabel(summary.other)}
        />
      ) : null}

      {/* Броят отчети е контекст към оборота, не показател с тренд — затова
          няма Δ чип: „с 50 % повече отчети" не значи нищо само по себе си. */}
      <ReportKpiCard
        kicker={t("reportsPage.dailyKpiReports")}
        value={String(summary.reports)}
        sub={t("reportsPage.dailyKpiReportsSub")}
      />
    </div>
  );
};
